<template>
  <form-request
    v-if="src"
    :action="src"
    method="post"
    v-slot="{ update, submit, errors }"
  >
    <popup-form
      title="Balance Package with Integration"
      button="Balance"
      :show="show"
      @close="$emit('close')"
      @confirm="adjustPackage(update, submit)"
    >
      <div class="input-group col-span-12 sm:col-span-4 mb-4">
        <label>Integration:</label>
        <select
          v-model="form.integration_id"
          name="integration_id"
        >
          <option
            :value="null"
            hidden
          >
            Select Integration
          </option>
          <template v-for="integration in integrations">
            <option :value="integration.id">{{ integration.display_name }}</option>
          </template>
        </select>
        <error
          :errors="errors"
          name="integration_id"
        />
      </div>

      <div class="input-group col-span-12 sm:col-span-4 mb-4">
        <label>Location:</label>
        <select
          v-model="form.location_id"
          name="location_id"
        >
          <option
            :value="null"
            hidden
          >
            Select Location
          </option>
          <template v-for="location in locations">
            <option :value="location.id">{{ location.name }}</option>
          </template>
        </select>
        <error
          :errors="errors"
          name="location_id"
        />
      </div>

      <div class="input-group col-span-12 sm:col-span-4 mb-4">
        <label>Space:</label>
        <select
          v-model="form.space_id"
          name="space_id"
        >
          <option
            :value="null"
            hidden
          >
            Select a Space
          </option>
          <template v-for="space in spaceList">
            <option :value="space.id">{{ space.name }}</option>
          </template>
        </select>
        <error
          :errors="errors"
          name="space_id"
        />
      </div>

      <div class="input-group col-span-12 sm:col-span-4 mb-4">
        <label>Sub-Lot:</label>
        <select
          v-model="form.sub_lot_num"
          name="sub_lot_num"
        >
          <option
            :value="null"
            hidden
          >
            Select Sub-Lot
          </option>
          <template v-for="val in subLotNums">
            <option :value="val">{{ val }}</option>
          </template>
        </select>
        <error
          :errors="errors"
          name="sub_lot_num"
        />
      </div>

      <div
        v-if="current.error"
        class="text-red-600 mb-4"
      >
        {{ current.error }}
      </div>

      <div
        v-if="current.quantity !== null"
        class="grid grid-cols-12 gap-4 py-2 items-center"
      >
        <div class="text-left flex flex-col leading-tight col-span-10">
          <span class="text-gray-800 text-sm font-medium">Current Available</span>
        </div>
        <div class="input-group col-span-2 text-center">
          <input
            :value="current.quantity"
            disabled
            type="number"
            step="1"
            min="0"
            :class="{ 'border-red-600 border-1.5 text-red-600': false, 'border border-gray-400 text-blue-600': true }"
          />
        </div>
      </div>

      <div
        v-if="current.integrationQuantity !== null"
        class="grid grid-cols-12 gap-4 py-2 items-center"
      >
        <div class="text-left flex flex-col leading-tight col-span-10">
          <span class="text-gray-800 text-sm font-medium">Available at Integration</span>
        </div>
        <div class="input-group col-span-2 text-center">
          <input
            :value="current.integrationQuantity"
            disabled
            type="number"
            step="1"
            min="0"
            :class="{ 'border-red-600 border-1.5 text-red-600': false, 'border border-gray-400 text-blue-600': true }"
          />
        </div>
      </div>

      <div
        v-if="form.quantity !== null"
        class="grid grid-cols-12 gap-4 py-2 items-center"
      >
        <div class="text-left flex flex-col leading-tight col-span-10">
          <span class="text-gray-800 text-sm font-medium">Set Integration To</span>
        </div>
        <div class="input-group col-span-2 text-center">
          <input
            v-model="form.quantity"
            type="number"
            step="1"
            min="0"
            :class="{ 'border-red-600 border-1.5 text-red-600': false, 'border border-gray-400 text-blue-600': true }"
          />
        </div>
      </div>
      <error
        :errors="errors"
        name="quantity"
      />
    </popup-form>
  </form-request>
</template>

<script>
import PopupForm from '@/Components/Forms/PopupForm'
import FormRequest from '@/Components/Forms/FormRequest'
import Error from '@/Components/Forms/Error'
import accessesCompany from '@/Mixins/accessesCompany'
import hasAuth from '@/Mixins/hasAuth'
import { getHeadersWithToken } from '@/utils/http'

export default {
  mixins: [accessesCompany, hasAuth],
  emits: ['close'],

  components: { PopupForm, FormRequest, Error },
  props: {
    /**
     * If true we display the form
     */
    show: {
      type: Boolean,
      default: false
    },
    package: {
      type: Object,
      required: true
    },
    integrations: {
      type: Array,
      default() {
        return []
      }
    },
    locations: {
      type: Array,
      default() {
        return []
      }
    },
    spaces: {
      type: Array,
      default() {
        return []
      }
    },
    src: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      // The form that gets submitted to the server
      form: {
        integration_id: null,
        location_id: null,
        space_id: null,
        sub_lot_num: null,
        quantity: null
      },
      subLotNums: [],
      current: {
        error: null,
        quantity: null,
        integrationQuantity: null
      }
    }
  },

  watch: {
    'form.location_id'(newValue) {
      this.form.space_id = null
      this.fetchSubLotOptions()
      this.fetchCurrentBalance()
    },
    'form.integration_id'(newValue) {
      this.fetchCurrentBalance()
    },
    'form.space_id'(newValue) {
      this.fetchCurrentBalance()
    },
    'form.sub_lot_num'(newValue) {
      this.fetchCurrentBalance()
    }
  },

  computed: {
    spaceList() {
      if (!this.form.location_id) {
        return []
      }

      return this.spaces.filter((space) => space.location_id === this.form.location_id)
    }
  },

  methods: {
    async fetchSubLotOptions() {
      console.log('fetching sub-lot nums', { packages: [this.package.id], location_id: this.form.location_id })

      const response = await fetch(
        route('api.packages.sub_lot_options', { packages: [this.package.id], location_id: this.form.location_id }),
        {
          headers: getHeadersWithToken(),
          method: 'get'
        }
      )

      let options = await response.json()
      this.subLotNums = options[this.package.id] || []
    },
    async fetchCurrentBalance() {
      this.form.quantity = this.current.quantity = this.current.integrationQuantity = null

      if (!this.form.location_id || !this.form.integration_id || !this.form.space_id || !this.form.sub_lot_num) {
        return
      }

      const response = await fetch(route('api.packages.current_balance', { package: this.package.id }), {
        headers: getHeadersWithToken(),
        method: 'post',
        body: JSON.stringify(this.form)
      })

      let data = await response.json()

      if (!response.ok) {
        this.current.error = data.message || 'An error has occurred'
        return
      }

      this.current.error = null
      this.current.quantity = data.available
      this.current.integrationQuantity = data.integration_available
      this.form.quantity = this.current.quantity
    },
    /**
     * Submits transfer request to the server
     * @param update a function (passed by form-request) that sets the values to send to the server
     * @param submit a function (passed by form-request) that submits the form
     */
    adjustPackage(update, submit) {
      this.$emit('close')
      update({ ...this.form })
      submit()
    }
  }
}
</script>
